import {type PartialOptions, UI, type UIElement} from "../../stem-core/ui/UIBase";
import type {Constructor} from "../../stem-core/base/Utils";
import {registerStyle, styleRule, styleRuleInherit, StyleSheet} from "../../stem-core/ui/Style";
import {Level} from "../../stem-core/ui/Constants";
import {Button} from "../../stem-core/ui/button/Button";
import {DownloadIcon} from "../../core/ui/SVGElements.jsx";
import {ButtonStyle} from "../../stem-core/ui/button/ButtonStyle";
import type {NodeAttributes} from "../../stem-core/ui/NodeAttributes";

class FileResourceStyle extends StyleSheet {
    @styleRule
    container = {
        cursor: "pointer",
    }
}


interface FileResourceLinkOptions {
    fileResource?: any;
    disabled?: boolean;
}

export const FileResourceLinkMixin = <T extends Constructor<UIElement<any, any, any>>>(BaseClass: T) => class FileResourceLink extends BaseClass {
    declare options: InstanceType<T>["options"] & FileResourceLinkOptions;

    getDefaultOptions(options?: this["options"]): Record<string, any> | undefined {
        return {
            ...super.getDefaultOptions(options),
            className: FileResourceStyle.getInstance().container,
            target: "_self",
        }
    }

    async triggerFileDownload() {
        const fileResource = this.getFileResource();
        // When triggering a download, we'll turn into a regular link
        // TODO it's possible the URL expired, in which case we'd need to get it again.
        if (!fileResource || this.options.href) {
            return;
        }
        const url = await fileResource.getURL();
        // Update the URL.
        this.updateOptions({href: url});
        // Simulate a click and trigger the download.
        // Even if somehow the browser blocks this (shouldn't user triggered flow), at least the URL would be correct later.
        // TODO maybe accept an event here, and duplicate that (Ctrl + Click for instance)
        this.node.click();
    }

    getFileResource() {
        return this.options.fileResource;
    }

    onMount() {
        this.addClickListener(() => this.triggerFileDownload());
    }
}

export const FileResourceLink = FileResourceLinkMixin(UI.Primitive("a"));


class DownloadButtonStyle extends ButtonStyle {
    @styleRuleInherit
    container = {
        ":focus": {
            backgroundColor: "none",
        }
    }

    @styleRuleInherit
    secondary = {
        ":active": {
            backgroundColor: "none",
        },
        ":focus": {
            backgroundColor: "none",
        },
        ":visited": {
            backgroundColor: "none",
        },
    };

    @styleRule
    disabled = {
        cursor: "not-allowed !important",
    }
}

// Named rather than written into the extends clause: Primitive answers with whatever base it was
// given, and inference for that only runs when the call isn't an argument being inferred itself
const ButtonLink = UI.Primitive("a", Button);

@registerStyle(DownloadButtonStyle)
export class DownloadButton extends FileResourceLinkMixin(ButtonLink) {
    getDefaultOptions(options?: PartialOptions<DownloadButton>): PartialOptions<DownloadButton> {
        return {
            ...super.getDefaultOptions(options),
            level: Level.SECONDARY,
        }
    }

    getIcon() {
        return <DownloadIcon style={{paddingRight: 8, verticalAlign: "middle"}} />;
    }

    extraNodeAttributes(attr: NodeAttributes) {
        super.extraNodeAttributes(attr);
        if (this.options.disabled) {
            attr.addClass(this.styleSheet.disabled);
        }
    }
}
